import React, { useEffect, useRef } from 'react';
import { useDispatch, batch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import Icon from '../../../../shared/_TBComponents/Icon';
import AthleteResultsBaseGrid from './AthleteResultsBaseGrid';
import {
  setSharedAthlete,
  setActiveWorkoutDate,
} from '../../../workouts/ducks/workoutsActions';

const SetCell = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px;
  align-items: stretch;
  justify-content: center;
`;

const DataLine = styled.div`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: ${(props) => (props.bold ? '700' : '400')};
  color: ${(props) => (props.assigned ? '#9e9e9e' : '#222')};
  text-align: center;
  padding: 2px 0;
  min-width: 80px;
  display: inline-flex;
  justify-content: center;
  grid-column: ${(props) => (props.spanAll ? '1 / -1' : 'auto')};
`;

const CompletedData = styled.div`
`;

const AssignedData = styled.div`
`;

const DataRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 8px;
  justify-items: center;
  align-items: center;
`;

const CompletedValue = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #222;
  background: ${(props) => {
    if (props.positive) return 'rgba(13, 204, 138, 0.1)';
    if (props.negative) return 'rgba(255, 5, 5, 0.1)';
    return 'transparent';
  }};
  padding: 2px 0;
  min-width: 80px;
  border-radius: 4px;
  display: inline-flex;
  justify-content: center;
  grid-column: ${(props) => (props.spanAll ? '1 / -1' : 'auto')};
`;

const TableWrapper = styled.div`
  width: 100%;
  font-family: 'Nunito Sans';
  --grid-border-color: #BDBDBD;

  .tableWrapper {
    border-radius: 8px;
    overflow: auto;
    max-width: 100%;
  }

  & .sticky {
    overflow: auto;
    
    [data-sticky-td] {
      position: sticky;
    }

    [data-sticky-last-left-td] {
    }
  }

  table {
    border-collapse: separate;
    width: max-content;
    min-width: 100%;
    border-spacing: 0;
    table-layout: auto;

    th, td {
      padding: 8px 16px;
      vertical-align: middle;
      background: white;
      margin: 0;
      border-right: 1px solid var(--grid-border-color);
      border-bottom: 1px solid var(--grid-border-color);
    }

    th:last-child,
    td:last-child {
      border-right: none;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    th {
      background-color: #EEE;
      font-weight: 600;
      font-size: 16px;
      vertical-align: middle;
      min-height: 48px;
      height: 48px;
      padding: 8px 16px;
    }

    td {
      text-align: center;
    }
    th:not(:first-child),
    td:not(:first-child) {
      min-width: 180px;
      text-align: center;
    }

    thead {
      th {
      }

      th:first-child {
        text-align: left;
        width: 220px !important;
        min-width: 220px !important;
        max-width: 220px !important;
      }
    }

    tbody {
      td:first-child {
        text-align: left;
        background: white;
        z-index: 1;
        width: 220px !important;
        min-width: 220px !important;
        max-width: 220px !important;
        vertical-align: middle;
        cursor: pointer;

        &:hover .exerciseLink {
          color: #77A7FF !important;
        }
      }
    }
  }
`;

const TableSection = styled.div`
  margin-bottom: 24px;
  border: 1px solid ${(props) => (props.isHighlighted ? '#ff6600' : '#BDBDBD')};
  border-radius: 8px;
  background: ${(props) => (props.isHighlighted ? '#fff3e0' : '#fafafa')};
  transition: border-color 2.5s ease, background-color 2.5s ease;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ExerciseCell = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  padding: 4px 8px;
  width: 100%;
  overflow: hidden;
`;

const ExerciseName = styled.span`
  font-weight: 500;
  color: inherit;
  font-size: 16px;
`;

const AssignedExerciseName = styled.span`
  font-size: 16px;
  color: inherit;
`;

const ItemSourceLine = styled.div`
  font-size: 16px;
  color: #9e9e9e;
`;

const VBTDataLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #9e9e9e;
  margin-top: 2px;
`;

// Data/value helpers section //

// Determine max number of set columns
const getMaxSets = (exercises) => {
  let maxSets = 0;
  exercises.forEach((exercise) => {
    const setKeys = Object.keys(exercise).filter((key) => /^set\d+$/.test(key));
    maxSets = Math.max(maxSets, setKeys.length);
  });
  return maxSets;
};

// Distinguish between no value and zero then get diffs
const parseValue = (valueString) => {
  if (!valueString && valueString !== '0' && valueString !== 0) return null;
  const cleaned = String(valueString).toLowerCase().replace(/[^\d.]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseRange = (valueString) => {
  if (!valueString) return null;
  const match = String(valueString).trim().match(/^(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
  if (!match) return null;
  return { min: parseFloat(match[1]), max: parseFloat(match[2]) };
};

const compareMetric = (completedValue, assignedValue) => {
  if (assignedValue === null || assignedValue === undefined || assignedValue === '') return null;

  const completed = parseValue(completedValue);
  if (completed === null) return 'neutral';

  const range = parseRange(assignedValue);
  if (range) {
    if (completed < range.min) return 'negative';
    if (completed > range.max) return 'positive';
    return 'neutral';
  }

  const assigned = parseValue(assignedValue);
  if (assigned === null) return null;

  if (completed > assigned) return 'positive';
  if (completed < assigned) return 'negative';
  return 'neutral';
};

//  Cell render helpers section //

const hasValue = (val) => val && String(val).trim() !== '';

const renderQuestionnaireCell = (setData) => (
  <SetCell>
    <CompletedData>
      <DataLine>{setData.completed.question}</DataLine>
      <DataLine>{setData.completed.answer || '-'}</DataLine>
    </CompletedData>
  </SetCell>
);

const renderHealthCell = (setData) => (
  <SetCell health>
    <CompletedData>
      <DataLine>{setData.completed.load || ''}</DataLine>
    </CompletedData>
  </SetCell>
);

const renderProgressionCell = (setData) => (
  <SetCell>
    <CompletedData>
      <DataLine>{setData.completed.progress || '-'}</DataLine>
    </CompletedData>
    <AssignedData>
      {setData.assigned.step ? <DataLine assigned>{setData.assigned.step}</DataLine> : null}
    </AssignedData>
  </SetCell>
);

const renderCircuitCell = (setData) => {
  const completedRounds = setData.completed.reps || setData.completed.load;
  const assignedRounds = setData.assigned.reps || setData.assigned.load;

  return (
    <SetCell>
      <CompletedData>
        <DataLine>
          {completedRounds || '-'}
        </DataLine>
      </CompletedData>
      <AssignedData>
        <DataRow>
          {!!assignedRounds && (
            <DataLine assigned spanAll={!setData.assigned.setRest}>
              {assignedRounds}
            </DataLine>
          )}
          {!!setData.assigned.setRest && (
            <DataLine assigned spanAll={!assignedRounds}>
              {setData.assigned.setRest}
            </DataLine>
          )}
        </DataRow>
      </AssignedData>
    </SetCell>
  );
};

const renderNotCompletedCell = (setData) => {
  const hasAdditionalFields = !!(setData.assigned.pace || setData.assigned.repsRest
      || setData.assigned.setRest || setData.assigned.intensity
      || setData.assigned.mainInputType);

  const repsText = setData.assigned.reps || '';
  const loadText = setData.assigned.load || '';

  const showReps = String(repsText).trim() !== '';
  const showLoad = String(loadText).trim() !== '';
  const showAssignedRow = showReps || showLoad;
  const onlyOne = (showReps && !showLoad) || (!showReps && showLoad);

  return (
    <SetCell>
      <DataRow>
        <CompletedValue spanAll>-</CompletedValue>
      </DataRow>
      {showAssignedRow ? (
        <DataRow>
          {showReps ? (
            <DataLine assigned spanAll={onlyOne}>{repsText}</DataLine>
          ) : null}
          {showLoad ? (
            <DataLine assigned spanAll={onlyOne}>{loadText}</DataLine>
          ) : null}
        </DataRow>
      ) : null}
      {hasAdditionalFields && (
        <DataRow>
          {!!setData.assigned.pace && <DataLine assigned>{setData.assigned.pace}</DataLine>}
          {!!setData.assigned.repsRest
            && <DataLine assigned>{setData.assigned.repsRest}</DataLine>}
          {!!setData.assigned.setRest && <DataLine assigned>{setData.assigned.setRest}</DataLine>}
          {!!setData.assigned.intensity
            && <DataLine assigned>{setData.assigned.intensity}</DataLine>}
        </DataRow>
      )}
    </SetCell>
  );
};

const renderDefaultSetCell = (setData) => {
  const hasAdditionalFields = !!(setData.assigned.pace || setData.assigned.repsRest
    || setData.assigned.setRest || setData.assigned.intensity
    || setData.assigned.mainInputType);

  const loadComparison = compareMetric(setData.completed.load, setData.assigned.load);
  const repsComparison = compareMetric(setData.completed.reps, setData.assigned.reps);
  const isCompletionOnly = !setData.completed.reps && !setData.assigned.reps;

  const completedRepsText = setData.completed.reps || '';
  const completedLoadText = setData.completed.load || '';
  const hasCompletedRepsText = String(completedRepsText).trim() !== '';
  const hasCompletedLoadText = String(completedLoadText).trim() !== '';
  const showCompletedReps = !isCompletionOnly && hasCompletedRepsText;
  const showCompletedLoad = hasCompletedLoadText;
  const showAnyCompleted = showCompletedReps || showCompletedLoad;
  const onlyOneCompletedValue = (showCompletedReps && !showCompletedLoad)
    || (!showCompletedReps && showCompletedLoad);

  const hasCompletedReps = !!(setData.completed.reps && String(setData.completed.reps).trim() !== '');
  const hasCompletedLoad = !!(setData.completed.load && String(setData.completed.load).trim() !== '');
  const hasAssignedReps = !!(setData.assigned.reps && String(setData.assigned.reps).trim() !== '');
  const hasAssignedLoad = !!(setData.assigned.load && String(setData.assigned.load).trim() !== '');
  const shouldShowAssignedRow = hasAssignedReps || hasAssignedLoad
    || hasCompletedReps || hasCompletedLoad;

  let assignedRepsText = '';
  if (hasAssignedReps) {
    assignedRepsText = setData.assigned.reps;
  } else if (hasCompletedReps) {
    assignedRepsText = '-';
  }

  let assignedLoadText = '';
  if (hasAssignedLoad) {
    assignedLoadText = setData.assigned.load;
  } else if (hasCompletedLoad) {
    assignedLoadText = '-';
  }

  const showAssignedReps = !isCompletionOnly && String(assignedRepsText).trim() !== '';
  const showAssignedLoad = String(assignedLoadText).trim() !== '';
  const showAnyAssigned = showAssignedReps || showAssignedLoad;
  const onlyOneAssignedValue = (showAssignedReps && !showAssignedLoad)
    || (!showAssignedReps && showAssignedLoad);

  return (
    <SetCell>
      {showAnyCompleted ? (
        <DataRow>
          {showCompletedReps ? (
            <CompletedValue
              positive={repsComparison === 'positive'}
              negative={repsComparison === 'negative'}
              spanAll={onlyOneCompletedValue}
            >
              {completedRepsText}
            </CompletedValue>
          ) : null}
          {showCompletedLoad ? (
            <CompletedValue
              positive={loadComparison === 'positive'}
              negative={loadComparison === 'negative'}
              spanAll={onlyOneCompletedValue}
            >
              {completedLoadText}
            </CompletedValue>
          ) : null}
        </DataRow>
      ) : (
        <DataRow>
          <CompletedValue spanAll>-</CompletedValue>
        </DataRow>
      )}

      {shouldShowAssignedRow && showAnyAssigned ? (
        <DataRow>
          {showAssignedReps ? (
            <DataLine assigned spanAll={onlyOneAssignedValue}>
              {assignedRepsText}
            </DataLine>
          ) : null}
          {showAssignedLoad ? (
            <DataLine assigned spanAll={onlyOneAssignedValue}>
              {assignedLoadText}
            </DataLine>
          ) : null}
        </DataRow>
      ) : null}

      {hasAdditionalFields && (
        <DataRow>
          {!!setData.assigned.pace && <DataLine assigned>{setData.assigned.pace}</DataLine>}
          {!!setData.assigned.repsRest && <DataLine assigned>{setData.assigned.repsRest}</DataLine>}
          {!!setData.assigned.setRest && <DataLine assigned>{setData.assigned.setRest}</DataLine>}
          {!!setData.assigned.intensity
            && <DataLine assigned>{setData.assigned.intensity}</DataLine>}
        </DataRow>
      )}
    </SetCell>
  );
};

const renderCellContent = (setData, exerciseType) => {
  // Check if exercise is assigned but not completed at all
  const hasAssignedData = hasValue(setData.assigned.load) || hasValue(setData.assigned.reps);
  const hasCompletedData = hasValue(setData.completed.load) || hasValue(setData.completed.reps);
  const notCompleted = hasAssignedData && !hasCompletedData;

  // Exercise type specific rendering
  if (exerciseType === 'QUESTIONNAIRE' && setData.completed.question) {
    return renderQuestionnaireCell(setData);
  }
  if (exerciseType === 'HEALTH') {
    return renderHealthCell(setData);
  }
  if (exerciseType === 'PROGRESSION') {
    return renderProgressionCell(setData);
  }
  if (exerciseType === 'CIRCUIT') {
    return renderCircuitCell(setData);
  }
  if (notCompleted) {
    return renderNotCompletedCell(setData);
  }

  return renderDefaultSetCell(setData);
};

const getExercisesWithNameForDate = (dateData) => {
  const calendarExercises = dateData.exercises.map((ex) => ({
    ...ex,
    isCalendarExercise: true,
  }));

  const programExercises = dateData.dayBasedExercises && dateData.dayBasedExercises.length > 0
    ? dateData.dayBasedExercises.flatMap((program) => (program.exercises || []).map((ex) => ({
      ...ex,
      isProgramExercise: true,
      programId: program.programId,
      programTitle: program.programTitle,
      instanceId: program.instanceId,
      dayNum: program.dayNum,
    })))
    : [];

  const allExercises = [
    ...calendarExercises,
    ...programExercises,
  ];

  return allExercises.map((ex) => ({
    ...ex,
    exercise: ex.completedExercise || ex.assignedExercise,
  }));
};

const buildWorkoutHref = (exercise, date, athleteId) => {
  let url = '';
  if (exercise?.isProgramExercise && exercise.programId) {
    url = `/workouts?programId=${exercise.programId}`;
    if (exercise.instanceId) url += `&instanceId=${exercise.instanceId}`;
    if (exercise.dayNum !== undefined) url += `&dayNum=${exercise.dayNum}`;
  } else {
    url = `/workouts?date=${date}`;
  }

  if (athleteId) {
    url += `&athleteId=${athleteId}`;
  }

  return url;
};

const SetCellComponent = ({ value, row }) => {
  if (!value) return null;
  return renderCellContent(value, row.original.exerciseType);
};

SetCellComponent.propTypes = {
  value: PropTypes.shape({
    completed: PropTypes.shape({}),
    assigned: PropTypes.shape({}),
  }),
  row: PropTypes.shape({
    original: PropTypes.shape({
      exerciseType: PropTypes.string,
    }),
  }).isRequired,
};

SetCellComponent.defaultProps = {
  value: null,
};

const ExerciseCellComponent = ({ row }) => {
  const {
    completedExercise: completed,
    assignedExercise: assigned,
    hasVBTData,
    itemSource,
    isProgramExercise,
    programTitle,
  } = row.original;
  const showBoth = completed && assigned && completed !== assigned;
  const sourceLabel = isProgramExercise ? (programTitle || itemSource) : itemSource;

  return (
    <ExerciseCell>
      <ExerciseName title={completed || assigned}>
        {completed || assigned}
      </ExerciseName>
      {showBoth && (
        <AssignedExerciseName title={assigned}>
          {assigned}
        </AssignedExerciseName>
      )}
      {sourceLabel ? (
        <ItemSourceLine title={sourceLabel}>
          {sourceLabel}
        </ItemSourceLine>
      ) : null}
      {hasVBTData && (
        <VBTDataLine>
          <Icon icon='vbt-dash' size={12} color='#9E9E9E' styledProps={{ marginBottom: '2px' }} />
          VBT Data
        </VBTDataLine>
      )}
    </ExerciseCell>
  );
};

ExerciseCellComponent.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      completedExercise: PropTypes.string,
      assignedExercise: PropTypes.string,
      hasVBTData: PropTypes.bool,
      itemSource: PropTypes.string,
      isProgramExercise: PropTypes.bool,
      programTitle: PropTypes.string,
    }),
  }).isRequired,
};

const createColumns = (date, exercises) => {
  const maxSets = getMaxSets(exercises);

  const setColumns = Array.from({ length: maxSets }, (_, index) => {
    const i = index + 1;
    return {
      Header: `Set ${i}`,
      accessor: `set${i}`,
      id: `set${i}`,
      minWidth: 180,
      Cell: SetCellComponent,
    };
  });

  return [
    {
      Header: date,
      accessor: 'exercise',
      id: 'exercise',
      sticky: 'left',
      width: 220,
      minWidth: 220,
      maxWidth: 220,
      Cell: ExerciseCellComponent,
    },
    ...setColumns,
  ];
};

// Table render section //

// Results container section //

const AthleteResultsTable = ({
  workoutResults, targetDate, scrollContainerRef, athleteData,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const targetRef = useRef(null);

  // Redirects to workout entry with athlete and date
  const handleTableClick = (date, exerciseData) => {
    if (!athleteData) return;
    batch(() => {
      dispatch(setSharedAthlete(athleteData));
      dispatch(setActiveWorkoutDate(date));
    });

    // Build URL with optional program parameter (date not needed for programs)
    let url = '';
    if (exerciseData && exerciseData.isProgramExercise && exerciseData.programId) {
      url = `/workouts?programId=${exerciseData.programId}`;
      if (exerciseData.instanceId) url += `&instanceId=${exerciseData.instanceId}`;
      if (exerciseData.dayNum !== undefined) url += `&dayNum=${exerciseData.dayNum}`;
    } else {
      url = `/workouts?date=${date}`;
    }
    history.push(url);
  };

  // Scroll to target date when coming from Daily Brief cell click
  useEffect(() => {
    if (targetDate && targetRef.current && scrollContainerRef?.current) {
      setTimeout(() => {
        const targetElement = targetRef.current;
        const container = scrollContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        const scrollOffset = targetRect.top - containerRect.top + container.scrollTop - 20;

        container.scrollTo({
          top: scrollOffset,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, [targetDate, scrollContainerRef]);

  if (!workoutResults || !workoutResults.dates || workoutResults.dates.length === 0) {
    return null;
  }

  // Helper functions below for date filtering
  const isDateInTargetWeek = (dateString) => {
    if (!targetDate) return false;

    const date = new Date(dateString);
    const target = new Date(targetDate);

    const targetWeekStart = new Date(target);
    targetWeekStart.setDate(target.getDate() - target.getDay());
    targetWeekStart.setHours(0, 0, 0, 0);

    const targetWeekEnd = new Date(targetWeekStart);
    targetWeekEnd.setDate(targetWeekStart.getDate() + 6);
    targetWeekEnd.setHours(23, 59, 59, 999);

    return date >= targetWeekStart && date <= targetWeekEnd;
  };

  const datesWithData = workoutResults.dates.slice().reverse().filter((dateData) => {
    const hasExercises = dateData.exercises && dateData.exercises.length > 0;
    const hasDayBasedExercises = dateData.dayBasedExercises
    && dateData.dayBasedExercises.length > 0;
    return hasExercises || hasDayBasedExercises;
  });

  const hasTargetWeekMatch = targetDate
    ? datesWithData.some((dateData) => isDateInTargetWeek(dateData.date))
    : false;

  const getClosestDateIndex = () => {
    if (!targetDate || hasTargetWeekMatch) return -1;

    const target = new Date(targetDate).getTime();
    let closestIndex = 0;
    let closestDiff = Math.abs(new Date(datesWithData[0].date).getTime() - target);

    datesWithData.forEach((dateData, index) => {
      const diff = Math.abs(new Date(dateData.date).getTime() - target);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  const closestDateIndex = getClosestDateIndex();

  return (
    <TableWrapper>
      {datesWithData.map((dateData, index) => {
        const [year, month, day] = dateData.date.split('-');
        const formattedDate = new Date(year, month - 1, day).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
        });
        const exercisesWithName = getExercisesWithNameForDate(dateData);
        const columns = createColumns(formattedDate, exercisesWithName);

        // Highlight only if exact week match exists or find closest
        const isHighlighted = hasTargetWeekMatch && isDateInTargetWeek(dateData.date);
        const isFirstHighlighted = isHighlighted && (
          index === 0 || !isDateInTargetWeek(datesWithData[index - 1].date)
        );
        const isClosestDate = closestDateIndex === index;
        const buildHref = (exercise) => buildWorkoutHref(exercise, dateData.date, athleteData?.id);

        return (
          <TableSection
            key={dateData.date}
            isHighlighted={isHighlighted}
            ref={isFirstHighlighted || isClosestDate ? targetRef : null}
          >
            <AthleteResultsBaseGrid
              columns={columns}
              data={exercisesWithName}
              onExerciseClick={(exerciseData) => {
                const dateForWorkoutEntry = exerciseData?.isProgramExercise
                  ? dateData.date : exerciseData?.assignedDate;
                handleTableClick(dateForWorkoutEntry, exerciseData);
              }}
              buildHref={buildHref}
            />
          </TableSection>
        );
      })}
    </TableWrapper>
  );
};

AthleteResultsTable.propTypes = {
  workoutResults: PropTypes.shape({
    dates: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string.isRequired,
        exercises: PropTypes.arrayOf(
          PropTypes.shape({
            completedExercise: PropTypes.string,
            assignedExercise: PropTypes.string,
          }),
        ).isRequired,
      }),
    ),
  }),
  targetDate: PropTypes.string,
  scrollContainerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  athleteData: PropTypes.shape({
    id: PropTypes.number,
    first: PropTypes.string,
    last: PropTypes.string,
  }),
};

AthleteResultsTable.defaultProps = {
  workoutResults: null,
  targetDate: null,
  scrollContainerRef: null,
  athleteData: null,
};

export default AthleteResultsTable;
